import React from "react";
import { useTranslation } from "react-i18next";
import {
  type ConditionEntry,
  type ConditionGroupEntry,
  type ConditionLeaf,
} from "@/components/ConditionBuilder/types";
import { isConditionGroup } from "@/components/ConditionBuilder/utils/conditionTree";
import { formatConditionLeafValue } from "@/components/ConditionBuilder/utils/flattenConditionEntriesForDisplay";
import { getOptionLabel } from "@/components/ConditionBuilder/utils/optionLabel";
import "./ConditionNarrative.scss";

function categoryFromLeaf(leaf: ConditionLeaf): string {
  // attribute.type is set to the parent group id (e.g. "USER", "AGENT")
  // by the hierarchical dropdown when a sub-attribute is selected.
  return leaf.attribute?.type?.split(".")?.[0]?.toUpperCase() ?? "";
}

// ── SentenceNode — recursive condition-tree renderer ─────────────────────────

export interface SentenceNodeProps {
  entry: ConditionEntry;
  depth?: number;
  showParens?: boolean;
  /** When true, omit the attribute category band (used outside MCP). */
  hideCategory?: boolean;
  /**
   * Custom operator-label resolver. Defaults to the MCP i18n lookup (falling
   * back to the raw operator id). DomainPolicy passes one that prefers the
   * operator's readable `name`.
   */
  resolveOperatorLabel?: (leaf: ConditionLeaf) => string;
  /**
   * Custom category-band resolver. Defaults to `mcpRegistry.toolPolicy.attributeCategory`.
   * DomainPolicy passes one that uses `policyWizard.review.attributeCategory`.
   */
  resolveCategoryLabel?: (leaf: ConditionLeaf) => string;
}

export const SentenceNode: React.FC<SentenceNodeProps> = ({
  entry,
  depth = 0,
  showParens = false,
  hideCategory = false,
  resolveOperatorLabel,
  resolveCategoryLabel,
}) => {
  const { t } = useTranslation();

  if (isConditionGroup(entry)) {
    const group = entry as ConditionGroupEntry;
    const children = group.conditions ?? [];
    const combinator =
      typeof group.combinator === "string"
        ? group.combinator
        : ((group.combinator as unknown as { id?: string } | null | undefined)
            ?.id ?? "AND");
    const shouldShowParens = depth > 0 || showParens;

    return (
      <span className="tlx-condition-narrative__group">
        {shouldShowParens && (
          <span className="tlx-condition-narrative__paren" aria-hidden>
            (
          </span>
        )}
        {children.map((child, i) => (
          <React.Fragment key={child.id}>
            <SentenceNode
              entry={child}
              depth={depth + 1}
              hideCategory={hideCategory}
              resolveOperatorLabel={resolveOperatorLabel}
              resolveCategoryLabel={resolveCategoryLabel}
            />
            {i < children.length - 1 && (
              <span
                className={`tlx-condition-narrative__combinator tlx-condition-narrative__combinator--${combinator.toLowerCase()}`}
              >
                {combinator}
              </span>
            )}
          </React.Fragment>
        ))}
        {shouldShowParens && (
          <span className="tlx-condition-narrative__paren" aria-hidden>
            )
          </span>
        )}
      </span>
    );
  }

  const leaf = entry as ConditionLeaf;
  const category = hideCategory ? "" : categoryFromLeaf(leaf);
  const categoryLabel = !category
    ? ""
    : resolveCategoryLabel
      ? resolveCategoryLabel(leaf)
      : t(`mcpRegistry.toolPolicy.attributeCategory.${category}`, {
          defaultValue: category,
        });
  const attribute = leaf.attribute ? getOptionLabel(leaf.attribute) : "—";
  const operatorId = leaf.operator?.id?.toLowerCase();
  const operator = resolveOperatorLabel
    ? resolveOperatorLabel(leaf)
    : operatorId
      ? t(`mcpRegistry.toolPolicy.operatorLabel.${operatorId}`, {
          defaultValue: leaf.operator?.id,
        })
      : t("mcpRegistry.toolPolicy.operatorLabel.unknown");
  const matchWithLabel = leaf.matchWith?.name ?? null;
  const value = formatConditionLeafValue(leaf);

  return (
    <span className="tlx-condition-narrative__condition">
      {categoryLabel && (
        <span className="tlx-condition-narrative__category">
          {categoryLabel}
        </span>
      )}
      <span className="tlx-condition-narrative__attribute">{attribute}</span>
      <span className="tlx-condition-narrative__operator">{operator}</span>
      {matchWithLabel && (
        <span className="tlx-condition-narrative__match-with">
          {matchWithLabel}
        </span>
      )}
      <span className="tlx-condition-narrative__value">
        &ldquo;{value}&rdquo;
      </span>
    </span>
  );
};

// ── ConditionNarrativeBlock — "Access is granted if … ." wrapper ──────────────

export interface ConditionNarrativeBlockProps {
  entries: ConditionEntry[];
  topLevelCombinator?: string;
  /** Lead-in text; defaults to the MCP "Access is granted if" string. */
  prefix?: React.ReactNode;
  /** When true, omit the attribute category band on every leaf. */
  hideCategory?: boolean;
  /** Custom operator-label resolver (see {@link SentenceNodeProps}). */
  resolveOperatorLabel?: (leaf: ConditionLeaf) => string;
  /** Custom category-band resolver (see {@link SentenceNodeProps}). */
  resolveCategoryLabel?: (leaf: ConditionLeaf) => string;
}

export const ConditionNarrativeBlock: React.FC<
  ConditionNarrativeBlockProps
> = ({
  entries,
  topLevelCombinator = "AND",
  prefix,
  hideCategory = false,
  resolveOperatorLabel,
  resolveCategoryLabel,
}) => {
  const { t } = useTranslation();
  const topCombinator = topLevelCombinator.toUpperCase();
  const multipleEntries = entries.length > 1;

  return (
    <div className="tlx-condition-narrative__container">
      <span className="tlx-condition-narrative__prefix">
        {prefix ?? t("mcpRegistry.toolPolicy.narrativePrefix")}
      </span>
      {entries.map((entry, i) => (
        <React.Fragment key={entry.id}>
          <SentenceNode
            entry={entry}
            depth={0}
            showParens={multipleEntries && isConditionGroup(entry)}
            hideCategory={hideCategory}
            resolveOperatorLabel={resolveOperatorLabel}
            resolveCategoryLabel={resolveCategoryLabel}
          />
          {i < entries.length - 1 && (
            <span
              className={`tlx-condition-narrative__combinator tlx-condition-narrative__combinator--${topCombinator.toLowerCase()}`}
            >
              {topCombinator}
            </span>
          )}
        </React.Fragment>
      ))}
      <span className="tlx-condition-narrative__period" aria-hidden>
        .
      </span>
    </div>
  );
};
